import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Leaf, LogOut } from 'lucide-react';
import { useShellState } from '@/app/context/ShellContext';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getNavigationForPortal, getAvailablePortalsForUser } from '@/config/navigation.config';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/constants/routes';

export const Sidebar: React.FC = () => {
  const { isSidebarCollapsed, toggleSidebar, activePortal, setActivePortal } = useShellState();
  const { can, hasRole } = usePermission();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate(ROUTES.AUTH.LOGIN);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const availablePortals = getAvailablePortalsForUser(user);

  // Auto fallback to authorized portal if current activePortal is not permitted for the user's role
  useEffect(() => {
    if (availablePortals.length > 0 && !availablePortals.includes(activePortal)) {
      setActivePortal(availablePortals[0]!);
    }
  }, [availablePortals, activePortal, setActivePortal]);

  const navItems = getNavigationForPortal(activePortal);

  // Group items by group property
  const groupsMap: Record<string, typeof navItems> = {};
  navItems.forEach((item) => {
    if (!groupsMap[item.group]) {
      groupsMap[item.group] = [];
    }
    groupsMap[item.group]!.push(item);
  });

  return (
    <aside
      className={cn(
        'hidden lg:flex flex-col fixed inset-y-0 left-0 h-screen border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-all duration-300 z-40 select-none shadow-xs font-poppins',
        isSidebarCollapsed ? 'w-20' : 'w-64'
      )}
    >
      {/* Brand Header */}
      <div className="flex h-20 items-center justify-between px-5 border-b border-sidebar-border/60 shrink-0">
        {!isSidebarCollapsed ? (
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white font-extrabold text-lg shadow-md shadow-indigo-500/20 transform hover:scale-105 transition-transform shrink-0">
              <Leaf className="h-5 w-5 text-white" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-poppins text-lg font-extrabold tracking-tight text-sidebar-foreground leading-none">
                ProcureAI
              </span>
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-primary mt-1">
                Enterprise Core
              </span>
            </div>
          </div>
        ) : (
          <div className="flex h-10 w-10 mx-auto items-center justify-center rounded-xl bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white font-extrabold text-lg shadow-md shadow-indigo-500/20 shrink-0">
            <Leaf className="h-5 w-5 text-white" />
          </div>
        )}

        <button
          onClick={toggleSidebar}
          className="rounded-lg p-1.5 hover:bg-sidebar-accent text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors cursor-pointer"
          title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isSidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>

      {/* Navigation Items (Scrollable when list exceeds viewport) */}
      <nav className="flex-1 space-y-5 p-3.5 overflow-y-auto custom-scrollbar">
        {Object.entries(groupsMap).map(([groupName, items]) => {
          const filteredItems = items.filter((item) => {
            if (item.requiredRoles && !hasRole(item.requiredRoles)) return false;
            if (item.requiredPermissions && !item.requiredPermissions.some((p) => can(p))) return false;
            return true;
          });
          if (filteredItems.length === 0) return null;

          return (
            <div key={groupName} className="space-y-1">
              {!isSidebarCollapsed && (
                <h3 className="px-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-sidebar-foreground/50 font-poppins">
                  {groupName}
                </h3>
              )}
              {filteredItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        'relative flex items-center gap-3.5 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-all duration-200 group font-poppins',
                        isActive
                          ? 'bg-gradient-to-r from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white font-bold shadow-md shadow-indigo-500/25 scale-[1.01]'
                          : 'text-sidebar-foreground/75 hover:bg-primary/10 hover:text-primary hover:translate-x-1 active:scale-98'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {/* Active Indicator Left Capsule */}
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1.5 rounded-r-full bg-white shadow-xs" />
                        )}
                        <Icon
                          className={cn(
                            'h-5 w-5 flex-shrink-0 transition-transform duration-200',
                            isActive
                              ? 'scale-105 text-white'
                              : 'group-hover:scale-105 group-hover:text-primary'
                          )}
                        />
                        {!isSidebarCollapsed && (
                          <span className="flex-1 truncate font-poppins text-sm font-semibold tracking-normal">
                            {item.label}
                          </span>
                        )}
                        {!isSidebarCollapsed && item.badge && (
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-[10.5px] font-extrabold transition-all',
                              isActive
                                ? 'bg-white/25 text-white shadow-2xs backdrop-blur-md'
                                : 'bg-primary/15 text-primary group-hover:bg-primary/25'
                            )}
                          >
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Footer: Active Profile & Functional Logout Button */}
      <div className="border-t border-sidebar-border/60 p-3.5 space-y-2.5 shrink-0 bg-sidebar">
        {!isSidebarCollapsed && user && (
          <div className="flex items-center gap-3 px-2.5 py-2 rounded-xl bg-sidebar-accent/50 border border-sidebar-border/50">
            <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold text-xs uppercase border border-primary/25 shrink-0">
              {user.name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-bold text-foreground truncate font-poppins">{user.name}</span>
              <span className="text-[10px] text-foreground-muted truncate font-poppins capitalize">
                {user.role.toLowerCase().replace('_', ' ')}
              </span>
            </div>
          </div>
        )}

        <button
          onClick={handleLogout}
          className={cn(
            'w-full flex items-center justify-center gap-2 rounded-xl border border-rose-500/25 bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 text-xs font-bold transition-all cursor-pointer group shadow-2xs font-poppins',
            isSidebarCollapsed ? 'p-2.5' : 'py-2.5 px-3'
          )}
          title={user ? `Sign Out (${user.name})` : 'Sign Out of ProcureAI'}
        >
          <LogOut className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          {!isSidebarCollapsed && <span>Sign Out</span>}
        </button>
      </div>
    </aside>
  );
};
